import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Clock01Icon, ShieldUserIcon, UserGroup03Icon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { CandidateCheckScreens } from 'constants/candidate_screens';
import { buildNavPath as buildNavPathHelper } from 'helpers/candidate_application/form-page-helpers';
import { escapeHtml } from 'helpers/escape-html';
import { useQueryParams } from 'hooks/query-params';
import { useToast } from 'hooks/toast';
import { useCandidateApplicationUploadActions } from 'hooks/upload-actions';
import { useUserAgent } from 'hooks/user-agent';
import i18n from 'i18n';
import CandidatePage from 'layouts/CandidatePage/CandidatePage';
import numberToWords from 'number-to-words';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { shouldSubmitCandidate } from 'utils/checks-utils';
import { CustomButton } from 'views/CandidateApp/components/CustomButton/CustomButton';

import { Form } from 'components/Form/Form/Form';
import { IconComponent } from 'components/Icon/IconComponent';

import {
  createReplacementReferee,
  saveCustomReference,
  selectCandidateApplication,
  submitCandidateApplication,
} from 'api/candidate-application-slice';
import {
  useCreateFormFieldTranslationsMutation,
  useLazyGetUiTranslationsPublicQuery,
} from 'api/forms-api';

import styles from './CandidateCustomReference.module.scss';

const dotToNested = flat =>
  Object.entries(flat).reduce((acc, [key, val]) => {
    key.split('.').reduce((obj, part, i, arr) => {
      if (i === arr.length - 1) obj[part] = val;
      else obj[part] = obj[part] || {};
      return obj[part];
    }, acc);
    return acc;
  }, {});

const CandidateCustomReference = () => {
  const navigate = useNavigate();
  const [admin] = useQueryParams(['admin']);

  // Helper to build navigation path with admin parameter preserved
  const buildNavPath = path => buildNavPathHelper(path, admin);
  const dispatch = useDispatch();

  const { user_agent } = useUserAgent();
  const { token, replacement } = useParams();
  const { t } = useTranslation();
  const showToast = useToast();

  const {
    application: {
      candidate,
      brand,
      candidate_checks,
      check_types,
      forms,
      country,
      referees,
      check_values,
      company_name,
      company,
    },
    fetched,
  } = useSelector(selectCandidateApplication);

  const { getUpload, signUpload, createUpload, deleteUpload } =
    useCandidateApplicationUploadActions(token);

  const [createFormFieldTranslations] = useCreateFormFieldTranslationsMutation();
  const [getUiTranslations] = useLazyGetUiTranslationsPublicQuery();

  const originalLanguageRef = useRef(i18n.language);
  const [translatedFields, setTranslatedFields] = useState({});
  const [selectedLanguageCode, setSelectedLanguageCode] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);

  const [loading, setLoading] = useState(false);
  const [referenceCheck, setReferenceCheck] = useState(null);
  const [pendingReferees, setPendingReferees] = useState([]);
  const [refereeEmails, setRefereeEmails] = useState([]);
  const [refereeContacts, setRefereeContacts] = useState([]);
  const [showIntro, setShowIntro] = useState(true);

  const formRef = useRef(null);
  const isSubmittingRefereeRef = useRef(false);

  // Data
  const [currentReferee, setCurrentReferee] = useState(0);
  const [currentForm, setCurrentForm] = useState(null);

  // Set check
  useEffect(() => {
    if (!fetched) return;

    const referenceCheck = candidate_checks.find(check => check.type === 'custom_reference');
    setReferenceCheck(referenceCheck);

    if (replacement) {
      const refereeToReplace = (referees || [])?.find(r => r.id == replacement);
      const replacementReferee = (referees || [])?.find(r => r.id == refereeToReplace?.replaced_by);

      if (!replacementReferee) {
        setLoading(true);
        dispatch(
          createReplacementReferee({
            token,
            id: referenceCheck.id,
            refereeId: refereeToReplace.id,
          }),
        ).then(({ payload: replacementReferee }) => {
          setPendingReferees([replacementReferee]);
          setLoading(false);
        });
      } else {
        setPendingReferees([replacementReferee]);
      }
    } else {
      const pendingReferees = (referees || [])?.filter(
        referee => !referee?.replaced_by && !referee?.submitted_at && !referee?.canceled,
      );
      setPendingReferees(pendingReferees);
    }
  }, [fetched]);

  const finalizeCandidateSubmission = async () => {
    if (shouldSubmitCandidate(referenceCheck, candidate_checks, check_types)) {
      try {
        await dispatch(submitCandidateApplication({ token, user_agent })).unwrap();
      } catch {
        showToast(t('candidate.customReference.submitFailed'), '', 'error');
        return;
      }
      navigate(
        buildNavPath(`/form_submission/candidate/${CandidateCheckScreens.SUCCESS}/${token}`),
      );
    } else {
      navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
    }
  };

  // All referees already nominated (e.g. save succeeded but a previous visit's final submit
  // didn't) — nothing left to show, so just retry completing the submission.
  useEffect(() => {
    if (!referenceCheck || replacement) return;
    if (pendingReferees.length > 0) return;
    if (isSubmittingRefereeRef.current) return;

    isSubmittingRefereeRef.current = true;
    setLoading(true);
    finalizeCandidateSubmission().finally(() => {
      setLoading(false);
      isSubmittingRefereeRef.current = false;
    });
  }, [referenceCheck, pendingReferees.length, replacement]);

  // Set current form
  useEffect(() => {
    if (pendingReferees?.length < 1) return;

    const referee = pendingReferees[currentReferee];
    setCurrentForm((forms || []).find(form => form?.id === referee?.candidate_form_id));
  }, [currentReferee, forms, pendingReferees.length]);

  // Reset form
  useEffect(() => {
    if (!currentForm) return;

    formRef.current?.reset();
    setTranslatedFields({});
    setSelectedLanguageCode(null);
  }, [currentForm]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentReferee]);

  useEffect(() => {
    if (showIntro) {
      setRefereeEmails([]);
      setRefereeContacts([]);
    }
  }, [showIntro]);

  const nominatedReferees = useMemo(
    () =>
      (referees || []).filter(
        referee =>
          referee.submitted_at && !referee.replaced_by && !referee.canceled && !referee.deleted,
      ),
    [referees],
  );

  const candidateEmail = useMemo(() => {
    const emails = [];
    if (candidate?.email) emails.push(candidate.email);
    if (check_values?.values?.email) emails.push(check_values.values.email);
    nominatedReferees.forEach(referee => referee.email && emails.push(referee.email));
    return emails;
  }, [check_values, candidate, nominatedReferees]);

  const candidateContact = useMemo(() => {
    const contact = [];
    if (candidate?.contact_number) contact.push(candidate.contact_number);
    if (check_values?.values?.phone_number) contact.push(check_values.values.phone_number);
    nominatedReferees.forEach(
      referee => referee.contact_number && contact.push(referee.contact_number),
    );
    return contact;
  }, [check_values, candidate, nominatedReferees]);

  useEffect(() => {
    if (!currentForm?.translation_language_code) return;
    const langCode = currentForm.translation_language_code;
    getUiTranslations(langCode).then(({ data: uiStrings }) => {
      if (uiStrings) {
        i18n.addResourceBundle(langCode, 'translation', dotToNested(uiStrings), true, true);
        i18n.changeLanguage(langCode);
        setSelectedLanguageCode(langCode);
      }
    });
    return () => {
      i18n.changeLanguage(originalLanguageRef.current);
    };
  }, [currentForm?.id, currentForm?.translation_language_code]);

  const handleLanguageChange = useCallback(
    async targetLanguageCode => {
      setSelectedLanguageCode(targetLanguageCode);
      if (!targetLanguageCode) {
        setTranslatedFields({});
        i18n.changeLanguage(originalLanguageRef.current);
        return;
      }
      setIsTranslating(true);
      try {
        const map = await createFormFieldTranslations({
          id: currentForm.id,
          language_code: targetLanguageCode,
        }).unwrap();
        setTranslatedFields(
          Object.fromEntries(
            Object.keys(map).map(id => [
              id,
              {
                text: map[id].text ?? null,
                translated_description: map[id].translated_description ?? null,
                translated_options: map[id].translated_options ?? null,
              },
            ]),
          ),
        );
      } catch {}
      try {
        const { data: uiStrings } = await getUiTranslations(targetLanguageCode);
        if (uiStrings) {
          i18n.addResourceBundle(
            targetLanguageCode,
            'translation',
            dotToNested(uiStrings),
            true,
            true,
          );
          i18n.changeLanguage(targetLanguageCode);
        }
      } catch {}
      setIsTranslating(false);
    },
    [currentForm?.id, createFormFieldTranslations, getUiTranslations],
  );

  const handleStartForm = () => {
    setShowIntro(false);
  };

  const onSubmit = async (answers, dataWithLabels) => {
    if (isSubmittingRefereeRef.current) return;
    isSubmittingRefereeRef.current = true;

    const refereeData = Object.values(dataWithLabels || {});
    const email = (refereeData || [])?.find(label => label.locked && label.type === 'email')?.value;
    const contact = (refereeData || [])?.find(
      label => label.locked && label.type === 'phone_number',
    )?.value;
    if (email) {
      setRefereeEmails(prev => (prev?.includes(email) ? prev : [...prev, email]));
    }
    if (contact) {
      setRefereeContacts(prev => (prev?.includes(contact) ? prev : [...prev, contact]));
    }

    const _pendingReferees = (pendingReferees || [])?.map((referee, i) => {
      if (i !== currentReferee) return referee;

      return {
        ...referee,
        answers,
        ...(replacement && { replacement }),
      };
    });

    if (currentReferee < _pendingReferees?.length - 1) {
      formRef.current.reset();
      setPendingReferees(_pendingReferees);
      setCurrentReferee(index => Math.min(index + 1, _pendingReferees.length - 1));
      isSubmittingRefereeRef.current = false;
    } else {
      setLoading(true);
      try {
        try {
          await dispatch(
            saveCustomReference({
              token,
              id: referenceCheck.id,
              referees: _pendingReferees,
              user_agent,
            }),
          ).unwrap();
        } catch {
          showToast(t('candidate.customReference.saveFailed'), '', 'error');
          return;
        }

        await finalizeCandidateSubmission();
      } finally {
        setLoading(false);
        isSubmittingRefereeRef.current = false;
      }
    }
  };

  return (
    <CandidatePage
      loading={loading}
      token={token}
      withTopLogo
      brand={brand}
      replacement={replacement}>
      <CandidatePage.View>
        {referenceCheck && (
          <CandidatePage.Card className={styles.root}>
            {showIntro ? (
              // Intro Screen
              <div className="d-flex flex-column align-items-start u-gap-large u-margin-bottom--large u-width-100">
                <IconComponent icon={UserGroup03Icon} size="xxlarge" />
                <div className="d-flex flex-column align-items-start u-gap">
                  <h1 className="primary-text u-font-weight-500 u-margin-0">
                    {t('common.yourReferees')}
                  </h1>
                  {replacement && (
                    <p className="secondary-text u-font-size-medium u-font-weight-400">
                      {t('candidate.customReference.backupReferee')}
                    </p>
                  )}
                  {!replacement && (
                    <span className="secondary-text u-font-size-medium u-font-weight-400 u-sentence-case">
                      You need to nominate {pendingReferees.length}{' '}
                      {pendingReferees.length === 1 ? t('common.referee') : t('common.referees')} to
                      complete your {t('referee.referenceCheck')}.
                    </span>
                  )}
                </div>
                <CustomButton
                  className={cn(styles.startButton, 'button button--primary')}
                  brand={brand}
                  onClick={handleStartForm}
                  dataTestId="custom-reference-get-started">
                  Get Started
                </CustomButton>
              </div>
            ) : (
              // Form Screen
              <div className="d-flex flex-column align-items-start u-margin-bottom--large u-width-100">
                <Form
                  key={currentReferee}
                  candidateId={candidate.id}
                  country={country}
                  ref={formRef}
                  textParams={{
                    candidate: candidate.first_name,
                    candidateFirstName: candidate.first_name,
                    candidateFullName: candidate.name,
                    candidateLastName: candidate.last_name,
                    job: candidate.job_role,
                    companyName: brand.brand_name || company_name,
                  }}
                  form={currentForm}
                  rules={currentForm?.rules}
                  brand={brand}
                  buttonTitle={
                    pendingReferees.length === currentReferee + 1
                      ? t('common.finish')
                      : t('common.next')
                  }
                  onSubmit={onSubmit}
                  getCandidateUpload={getUpload}
                  deleteCandidateUpload={deleteUpload}
                  signCandidateUpload={signUpload}
                  createCandidateUpload={createUpload}
                  includeCandidateReferenceValidation
                  candidateEmail={[...candidateEmail, ...refereeEmails]}
                  candidateContact={[...candidateContact, ...refereeContacts]}
                  emailCompareErrorMessage={t('candidate.customReference.emailCompareErrorMessage')}
                  contactCompareErrorMessage={t(
                    'candidate.customReference.contactCompareErrorMessage',
                  )}
                  detectionEndpoint={'candidate_checks/token/' + token + '/language_detection'}
                  allowTranslation={!!company?.ff_translations}
                  selectedLanguageCode={selectedLanguageCode}
                  isTranslating={isTranslating}
                  onLanguageChange={handleLanguageChange}
                  translatedFields={translatedFields}
                  fieldRequiredVariantStyle="text"
                  header
                  headerIcon={UserGroup03Icon}
                  headerIconBg={
                    brand?.brand_active && brand?.color_button
                      ? brand.color_button
                      : 'linear-gradient(180deg, #9e4ff3 0%, #5d33bc 100%)'
                  }
                  headerTitleColor={
                    brand?.brand_active && brand?.color_button ? brand.color_button : '#7a48df'
                  }
                  headerTitle={
                    replacement
                      ? t('candidate.customReference.nominateBackupReferee')
                      : t('candidate.customReference.nominateOrdinalReferee', {
                          ordinal: numberToWords.toWordsOrdinal(currentReferee + 1),
                        })
                  }
                  headerSubtitle={
                    candidate.first_name
                      ? t('candidate.customReference.hiForApplication', {
                          name: candidate.first_name,
                          company: brand.brand_name || company_name,
                        })
                      : ''
                  }
                  headerDescription={
                    currentForm?.intro_text?.trim()
                      ? escapeHtml(currentForm.intro_text.trim())
                      : pendingReferees.length === 1
                        ? t('candidate.customReference.addSinglePersonDescription')
                        : t('candidate.customReference.addPeopleDescription', {
                            count: pendingReferees.length,
                          })
                  }
                  headerBadges={[
                    {
                      icon: UserGroup03Icon,
                      label: t('candidate.customReference.refereesNeeded', {
                        count: pendingReferees.length,
                      }),
                    },
                    { icon: Clock01Icon, label: '~2 ' + t('refereeForm.minutes') },
                    { icon: ShieldUserIcon, label: t('refereeForm.confidential') },
                  ]}
                  translationTitle={t('components.form.translateTheFormInto')}
                />
              </div>
            )}
          </CandidatePage.Card>
        )}
      </CandidatePage.View>
    </CandidatePage>
  );
};

export default CandidateCustomReference;
